import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { paddingCardsTables } from 'commons/values';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import Pagination from '@mui/material/Pagination';

import { useTableState } from '../../../../hooks/useTableState';
// import { getAllKnowledgeBases, deleteKnowledgeBase, restoreKnowledgeBase } from '../../../store/knowledge-bases';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../commons/generics/StyledTableComponents';
import { GenericTableHead } from '../../../../commons/generics/GenericTableHead';
import { SinDatos } from '../../../../commons/generics/SinDatos';
import {
  corregirTexto,
  firstLetterUpperCase,
  hasPemissions,
  usarFuncionesComposicion,
} from '../../../../commons/utils';
import { ModalConfirmDeactivation } from 'components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from 'components/modals/ModalConfirmRestore';
import { ClampedText } from 'commons/generics/ClampedText';
import { StatusChip } from 'commons/generics/StatusChip';
import {
  deleteKnowledgeBase,
  getAllKnowledgeBases,
  restoreKnowledgeBase,
} from 'store/knowledge-bases/thunks';

const headCells = [
  { id: 'name', label: 'Nombre' },
  { id: 'description', label: 'Descripción' },
  { id: 'embeddingModel', label: 'Modelo' },
  { id: 'vectorCount', label: 'Vectores' },
  { id: 'embeddingStatus', label: 'Procesamiento' },
  { id: 'status', label: 'Estado' },
  { id: 'actions', label: 'Acciones' },
];

export const TableBaseConocimiento = () => {
  const navigate = useNavigate();

  const { knowledge_bases_list } = useSelector((state: any) => state.knowledgeBases);
  const dispatch = useDispatch<any>();

  const {
    dataTableList,
    checked,
    searchTerm,
    setSearchTerm,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    openElem,
    setOpenElem,
    ResponseComponents,
    handleResponse,
    updatePaginationFromList,
    getSortedAndFilteredList,
    isServerSide,
    order,
    orderBy,
    handleRequestSort,
  } = useTableState('knowledgeBases');

  const [hasPermission, setHasPermission] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canRestore, setCanRestore] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    updatePaginationFromList(knowledge_bases_list);
  }, [knowledge_bases_list, updatePaginationFromList]);

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.inteligencia.artificial',
      'modulo.inteligencia.artificial.base.conocimiento',
      'view'
    );
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions(
      'modulo.inteligencia.artificial',
      'modulo.inteligencia.artificial.base.conocimiento',
      'edit'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.inteligencia.artificial',
      'modulo.inteligencia.artificial.base.conocimiento',
      'delete'
    );
    if (del) setCanDelete(true);

    const restore = await hasPemissions(
      'modulo.inteligencia.artificial',
      'modulo.inteligencia.artificial.base.conocimiento',
      'restore'
    );
    if (restore) setCanRestore(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getInitialData = async (statusVal = checked ? 0 : 1) => {
    const response = await dispatch(
      getAllKnowledgeBases({ page: 1, take: rowsPerPage, status: statusVal, param: '' })
    );
    await handleResponse(response, 'listar bases de conocimiento', false);
  };

  useEffect(() => {
    if (canView) {
      getInitialData();
    }
  }, [canView]);

  const handleView = (row: any) => {
    navigate(`/inteligencia-artificial/base-conocimiento/detalle/${row.id}`);
  };
  const handleEdit = (row: any) => {
    navigate(`/inteligencia-artificial/base-conocimiento/editar/${row.id}`);
  };

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const param = e.target.value;
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) =>
        getAllKnowledgeBases({ page: 1, take: rowsPerPage, status: checked ? 0 : 1, param: p }),
      getInitialData,
      null,
      'buscar bases de conocimiento'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;

    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getAllKnowledgeBases({ page: 1, take: rowsPerPage, status, param: p }),
        () => getInitialData(status),
        null,
        'buscar bases de conocimiento'
      );
    } else {
      getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getInitialData());
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    const validPage = value < 1 ? 1 : value;
    changePaginationPageHook(event, validPage);
    const response = await dispatch(
      getAllKnowledgeBases({
        page: validPage,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'listar bases de conocimiento', false);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      deleteKnowledgeBase(openElem.id, {
        page: pagePagination,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'desactivar base de conocimiento', true);
    setOpenModalDeactivation(false);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      restoreKnowledgeBase(openElem.id, {
        page: pagePagination,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'restaurar base de conocimiento', true);
    setOpenModalRestore(false);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3, mt: 5 }}>
        <Typography variant="h6" color="error">
          No tienes permisos para ver este contenido.
        </Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name'],
    'status',
    checked ? 0 : 1
  );

  return (
    <Box sx={{ width: '100%', mt: paddingCardsTables }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar base..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={inputValue}
          onChange={handleSearch}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <GenericTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headCells={headCells}
            />
            <TableBody>
              {sortedList.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={6}>
                    <SinDatos />
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell>
                        {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.name)}
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText
                          text={
                            usarFuncionesComposicion(
                              corregirTexto,
                              firstLetterUpperCase,
                              row.description
                            ) || 'Sin descripción'
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {usarFuncionesComposicion(
                          corregirTexto,
                          firstLetterUpperCase,
                          row.embeddingModel
                        ) || 'N/A'}
                      </StyledTableCell>
                      <StyledTableCell>{row.vectorCount || 0}</StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={
                            row.embeddingStatus === 'COMPLETED'
                              ? 'Procesado'
                              : row.embeddingStatus === 'PENDING'
                                ? 'En proceso'
                                : row.embeddingStatus || 'Pendiente'
                          }
                          color={row.embeddingStatus === 'COMPLETED' ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip status={row.status ? 1 : 0} />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canView && handleView(row)}
                              disabled={!canView}
                              color="info"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          <span
                            style={{ cursor: !canEdit || !row.status ? 'not-allowed' : 'pointer' }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => canEdit && row.status && handleEdit(row)}
                              disabled={!canEdit || !row.status}
                              color="primary"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          {row.status ? (
                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (canDelete) {
                                    setOpenElem(row);
                                    setOpenModalDeactivation(true);
                                  }
                                }}
                                disabled={!canDelete}
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          ) : (
                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (canRestore) {
                                    setOpenElem(row);
                                    setOpenModalRestore(true);
                                  }
                                }}
                                disabled={!canRestore}
                                color="success"
                              >
                                <RestoreOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {sortedList.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={isServerSide ? itemCountPagination : sortedList.length}
            rowsPerPage={rowsPerPage}
            page={isServerSide ? 0 : page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
          />
        )}

        {sortedList.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 1,
              padding: 2.5,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Pagination
              sx={{ justifyContent: 'center' }}
              color="primary"
              count={
                isServerSide
                  ? Math.ceil(itemCountPagination / rowsPerPage)
                  : Math.ceil(sortedList.length / rowsPerPage)
              }
              page={pagePagination}
              onChange={changePaginationPage}
              showFirstButton
              showLastButton
              shape="circular"
            />
          </Box>
        )}
      </Card>
      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        title="¿Estás seguro que deseas desactivar esta base?"
        description="Esta base pasará a estado inactivo."
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        title="¿Estás seguro que deseas restaurar esta base?"
        description="Esta base pasará a estado activo."
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
